import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Smile } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ReactionPickerProps {
  onReactionSelect: (emoji: string) => void
  disabled?: boolean
}

// Common emoji reactions
const COMMON_REACTIONS = [
  '👍', '👎', '❤️', '😂', '😮', '😢', '😡', '🎉', '💀', '🔥',
  '✨', '👀', '🙏', '💯', '🚀', '👏', '🤔', '😊', '💪', '✅'
]

const ReactionPicker = ({ onReactionSelect, disabled }: ReactionPickerProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleSelect = (emoji: string) => {
    onReactionSelect(emoji)
    setIsOpen(false)
  }

  // Calculate position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const pickerWidth = 280
      const pickerHeight = 100 // approximate height

      // Position above the button by default
      let top = rect.top - pickerHeight - 8
      let left = rect.left

      // Adjust if it would go off the left edge
      if (left < 10) {
        left = 10
      }

      // Adjust if it would go off the right edge
      if (left + pickerWidth > window.innerWidth - 10) {
        left = window.innerWidth - pickerWidth - 10
      }

      // Adjust if it would go off the top edge
      if (top < 10) {
        top = rect.bottom + 8 // Position below instead
      }

      setPosition({ top, left })
    }
  }, [isOpen])

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onPointerDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) {
            setIsOpen(!isOpen)
          }
        }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        disabled={disabled}
        className={cn(
          "flex items-center gap-1 px-1.5 py-0.5 rounded text-xs transition-all hover:bg-gray-200 dark:hover:bg-dark-muted border border-transparent",
          isOpen ? "bg-gray-200 dark:bg-dark-muted" : "bg-gray-100 dark:bg-dark-accent",
          disabled && "cursor-not-allowed opacity-50"
        )}
        aria-label="Add reaction"
      >
        <Smile size={14} className="text-gray-500 dark:text-dark-text-muted" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0"
              style={{ zIndex: 10001 }}
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(false)
              }}
            />

            {/* Picker popup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed p-2 rounded-lg shadow-lg border bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border"
              style={{
                width: '280px',
                zIndex: 10002,
                top: `${position.top}px`,
                left: `${position.left}px`,
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="text-xs font-medium mb-2 px-1 text-gray-600 dark:text-gray-400">
                Choose a reaction
              </div>
              <div className="grid grid-cols-10 gap-1">
                {COMMON_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onPointerDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSelect(emoji)
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    className="w-[24px] h-[24px] flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-dark-accent hover:scale-[1.2] transition-all text-lg"
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ReactionPicker
